import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Plus, Trash2, Save, Download, Eye, EyeOff, Move, QrCode } from 'lucide-react';
import axios from 'axios';
import '../../components/diseñocss/ConfiguradorEtiquetas.css';

const ConfiguradorEtiquetas = ({ isOpen, onClose, onSaveConfig, pedidosReales = [] }) => {
    const LABEL_WIDTH = 100; // mm
    const LABEL_HEIGHT = 50; // mm
    const DPI = 203; // Zebra ZD421 DPI

    // Carousel card sizing (px) - must match CSS .plantilla-card-integrada width + margin
    const CARD_WIDTH = 160;
    const CARD_MARGIN = 12; // left+right total (6+6)
    const CARD_STEP = CARD_WIDTH + CARD_MARGIN;

    const CAMPOS_DISPONIBLES = [
        { id: 'header', label: 'Encabezado (CIASA)', tipo: 'texto' },
        { id: 'nombre', label: 'Nombre', tipo: 'dato' },
        { id: 'apellido', label: 'Apellido', tipo: 'dato' },
        { id: 'plato', label: 'Plato', tipo: 'dato' },
        { id: 'sector', label: 'Sector', tipo: 'dato' },
        { id: 'ubicacion', label: 'Ubicación', tipo: 'dato' },
        { id: 'sede', label: 'Sede', tipo: 'dato' },
        { id: 'fechaVencimiento', label: 'Fecha Vencimiento', tipo: 'dato' },
        { id: 'separador', label: 'Línea Separadora', tipo: 'visual' },
        { id: 'qrCode', label: 'Código QR', tipo: 'qr' },
        { id: 'barcode', label: 'Código de Barras', tipo: 'barcode' },
    ];

    const [campos, setCampos] = useState([
        { id: 'header', label: 'CIASA', x: 10, y: 5, fontSize: 30, visible: true, rotation: 0 },
        { id: 'nombre', label: 'Nombre', x: 10, y: 15, fontSize: 20, visible: true, prefijo: 'Nombre: ', rotation: 0 },
        { id: 'apellido', label: 'Apellido', x: 10, y: 20, fontSize: 20, visible: true, prefijo: 'Apellido: ', rotation: 0 },
        { id: 'sector', label: 'Sector', x: 10, y: 25, fontSize: 18, visible: true, prefijo: 'Sector: ', rotation: 0 },
        { id: 'ubicacion', label: 'Ubicación', x: 10, y: 30, fontSize: 18, visible: true, prefijo: 'Ubicación: ', rotation: 0 },
        { id: 'plato', label: 'Plato', x: 10, y: 35, fontSize: 16, visible: true, prefijo: '', rotation: 0 },
        { id: 'fechaVencimiento', label: 'Vence', x: 10, y: 42, fontSize: 14, visible: true, prefijo: 'Vence: ', rotation: 0 },
    ]);

    const [selectedCampo, setSelectedCampo] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [plantillaNombre, setPlantillaNombre] = useState('');
    const [showPreview, setShowPreview] = useState(true);
    const [plantillasGuardadas, setPlantillasGuardadas] = useState([]);
    const [pedidoPreviewIndex, setPedidoPreviewIndex] = useState(0);
    const [showNombreModal, setShowNombreModal] = useState(false);
    const [plantillaActiva, setPlantillaActiva] = useState(0);
    const [previewScale, setPreviewScale] = useState(1);
    const previewContainerRef = useRef(null);
    const [previewFading, setPreviewFading] = useState(false);
    const fadeTimeoutRef = useRef(null);

    // Estados para navegación con mouse (carousel simple)
    const [isDraggingCarousel, setIsDraggingCarousel] = useState(false);
    const [dragCurrentX, setDragCurrentX] = useState(0);
    const [carouselOffset, setCarouselOffset] = useState(0);

    // Cargar configuración guardada y plantillas desde el backend
    useEffect(() => {
        const savedConfig = localStorage.getItem('etiquetaConfig');
        if (savedConfig) {
            try {
                const config = JSON.parse(savedConfig);
                setCampos(config.campos || campos);
            } catch (error) {
                console.error('Error al cargar configuración:', error);
            }
        }

        // Cargar plantillas desde el backend
        const cargarPlantillasDesdeBackend = async () => {
            try {
                const token = sessionStorage.getItem('token');
                if (!token) {
                    console.warn('No hay token de sesión');
                    return;
                }

                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/plantillas-etiquetas`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                if (response.data && Array.isArray(response.data)) {
                    setPlantillasGuardadas(response.data);
                }
            } catch (error) {
                console.error('Error al cargar plantillas desde backend:', error);
                // Fallback a localStorage si falla
                const savedPlantillas = localStorage.getItem('etiquetaPlantillas');
                if (savedPlantillas) {
                    try {
                        setPlantillasGuardadas(JSON.parse(savedPlantillas));
                    } catch (e) {
                        console.error('Error al cargar plantillas desde localStorage:', e);
                    }
                }
            }
        };

        cargarPlantillasDesdeBackend();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const agregarCampo = (campoId) => {
        const campoDisponible = CAMPOS_DISPONIBLES.find(c => c.id === campoId);
        if (!campoDisponible) return;

        const nuevoCampo = {
            id: campoId,
            label: campoDisponible.label,
            x: 10,
            y: 10,
            fontSize: 16,
            visible: true,
            prefijo: '',
            rotation: 0,
            qrSize: 30, // Para códigos QR
            barcodeWidth: 2, // Para códigos de barras
            barcodeHeight: 50,
        };

        setCampos([...campos, nuevoCampo]);
    };

    const eliminarCampo = (index) => {
        const nuevosCampos = campos.filter((_, i) => i !== index);
        setCampos(nuevosCampos);
        if (selectedCampo === index) setSelectedCampo(null);
    };

    const actualizarCampo = (index, propiedad, valor) => {
        const nuevosCampos = [...campos];
        nuevosCampos[index] = { ...nuevosCampos[index], [propiedad]: valor };
        setCampos(nuevosCampos);
    };

    const toggleVisibilidad = (index) => {
        actualizarCampo(index, 'visible', !campos[index].visible);
    };

    const handleMouseDown = (index, e) => {
        e.preventDefault();
        setSelectedCampo(index);
        setIsDragging(true);
    };

    const handleMouseMove = useCallback((e) => {
        if (!isDragging || selectedCampo === null) return;

        const preview = document.getElementById('label-preview');
        if (!preview) return;

        const rect = preview.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * LABEL_WIDTH;
        const y = ((e.clientY - rect.top) / rect.height) * LABEL_HEIGHT;

        setCampos(prevCampos => {
            const nuevosCampos = [...prevCampos];
            nuevosCampos[selectedCampo] = {
                ...nuevosCampos[selectedCampo],
                x: Math.max(0, Math.min(LABEL_WIDTH - 5, x)),
                y: Math.max(0, Math.min(LABEL_HEIGHT - 2, y))
            };
            return nuevosCampos;
        });
    }, [isDragging, selectedCampo]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // ajustar escala de preview para mantener proporciones en pantallas pequeñas
    useEffect(() => {
        const computeScale = () => {
            const container = previewContainerRef.current;
            if (!container) return setPreviewScale(1);
            const available = container.clientWidth; // width of preview area
            const base = 420; // base width including padding we used visually
            const scale = Math.min(1, Math.max(0.4, available / base));
            setPreviewScale(scale);
        };
        computeScale();
        window.addEventListener('resize', computeScale);
        return () => window.removeEventListener('resize', computeScale);
    }, [previewContainerRef]);

    const guardarConfiguracion = () => {
        const config = { campos };
        localStorage.setItem('etiquetaConfig', JSON.stringify(config));
        if (onSaveConfig) onSaveConfig(config);
        alert('✅ Configuración guardada correctamente');
    };

    const guardarComoPlantilla = async () => {
        if (!plantillaNombre.trim()) {
            alert('⚠️ Por favor ingresa un nombre para la plantilla');
            return;
        }

        // Validar que no exista una plantilla con el mismo nombre
        const nombreExistente = plantillasGuardadas.find(
            p => p.nombre.toLowerCase().trim() === plantillaNombre.toLowerCase().trim()
        );

        if (nombreExistente) {
            alert('⚠️ Ya existe una plantilla con ese nombre. Por favor elige otro nombre.');
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            if (!token) {
                alert('⚠️ Debes iniciar sesión para guardar plantillas');
                return;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/plantillas-etiquetas`,
                {
                    nombre: plantillaNombre,
                    campos: campos
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const nuevaPlantilla = response.data;
            const plantillasActualizadas = [...plantillasGuardadas, nuevaPlantilla];
            setPlantillasGuardadas(plantillasActualizadas);

            // Backup en localStorage
            localStorage.setItem('etiquetaPlantillas', JSON.stringify(plantillasActualizadas));

            setPlantillaNombre('');
            setShowNombreModal(false);
            alert(`✅ Plantilla "${nuevaPlantilla.nombre}" guardada correctamente`);
        } catch (error) {
            console.error('Error al guardar plantilla:', error);
            alert('❌ Error al guardar la plantilla. Intenta nuevamente.');
        }
    };

    const abrirModalNombre = () => {
        setPlantillaNombre('');
        setShowNombreModal(true);
    };

    const cargarPlantilla = (plantilla) => {
        aplicarPlantilla(plantilla, false);
    };

    // Aplica una plantilla, con opción silent para no mostrar alertas (útil cuando se navega)
    const aplicarPlantilla = (plantilla, silent = true) => {
        if (!plantilla) return;
        setCampos(plantilla.campos);
        // Guardar la configuración cargada en localStorage
        const config = { campos: plantilla.campos };
        localStorage.setItem('etiquetaConfig', JSON.stringify(config));
        // Notificar al padre (AbmPedidos) para que actualice su estado inmediatamente
        // Only notify parent when not applying silently (so navigating templates doesn't close the modal)
        if (!silent && onSaveConfig) onSaveConfig(config);
        if (!silent) alert(`✅ Plantilla "${plantilla.nombre}" cargada y aplicada`);
    };

    const navegarPlantilla = useCallback((direccion) => {
        if (direccion === 'next') {
            setPlantillaActiva((prev) => (prev + 1) % plantillasGuardadas.length);
        } else {
            setPlantillaActiva((prev) => (prev - 1 + plantillasGuardadas.length) % plantillasGuardadas.length);
        }
    }, [plantillasGuardadas.length]);



    // Cuando cambia la plantilla activa (por select o flechas), aplicar silenciosamente la plantilla
    useEffect(() => {
        if (plantillasGuardadas.length === 0) return;
        const plantilla = plantillasGuardadas[plantillaActiva];
        if (!plantilla) return;

        // small fade animation: fade out, swap content, fade in
        // clear any existing timers
        if (fadeTimeoutRef.current) {
            clearTimeout(fadeTimeoutRef.current);
        }
        setPreviewFading(true);
        // wait for fade-out (180ms) then apply silently and fade in
        fadeTimeoutRef.current = setTimeout(() => {
            aplicarPlantilla(plantilla, true);
            // small delay to allow DOM update then fade in
            fadeTimeoutRef.current = setTimeout(() => {
                setPreviewFading(false);
            }, 60);
        }, 180);

        return () => {
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
                fadeTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plantillaActiva, plantillasGuardadas]);

    // Funciones para navegación con mouse - Efecto esfera circular
    const handleCarouselMouseDown = (e) => {
        setIsDraggingCarousel(true);
        setDragCurrentX(e.clientX);
    };

    const handleCarouselMouseMove = useCallback((e) => {
        if (!isDraggingCarousel) return;
        const deltaX = e.clientX - dragCurrentX;
        // Mover el track horizontalmente según deltaX
        setCarouselOffset(prev => prev + deltaX);
        setDragCurrentX(e.clientX);
    }, [isDraggingCarousel, dragCurrentX]);


    const handleCarouselMouseUp = useCallback(() => {
        if (!isDraggingCarousel) return;

        const totalPlantillas = plantillasGuardadas.length;
        if (totalPlantillas === 0) return;

        // Snap to nearest index based on carouselOffset
        setIsDraggingCarousel(false);
        // compute index
        const index = Math.round(-carouselOffset / CARD_STEP);
        const nuevoIndice = Math.max(0, Math.min(totalPlantillas - 1, index));
        setPlantillaActiva(nuevoIndice);
        // reset drag state
        setDragCurrentX(0);
    }, [isDraggingCarousel, carouselOffset, plantillasGuardadas.length, CARD_STEP]);

    const handleCarouselWheel = useCallback((e) => {
        e.preventDefault();
        if (plantillasGuardadas.length <= 1) return;

        if (e.deltaY > 0) {
            // Scroll hacia abajo -> siguiente
            setPlantillaActiva((prev) => (prev + 1) % plantillasGuardadas.length);
        } else {
            // Scroll hacia arriba -> anterior
            setPlantillaActiva((prev) => (prev - 1 + plantillasGuardadas.length) % plantillasGuardadas.length);
        }
    }, [plantillasGuardadas.length]);

    // Agregar listeners para drag
    useEffect(() => {
        if (isDraggingCarousel) {
            document.addEventListener('mousemove', handleCarouselMouseMove);
            document.addEventListener('mouseup', handleCarouselMouseUp);
        }
        return () => {
            document.removeEventListener('mousemove', handleCarouselMouseMove);
            document.removeEventListener('mouseup', handleCarouselMouseUp);
        };
    }, [isDraggingCarousel, handleCarouselMouseMove, handleCarouselMouseUp]);

    // Cuando cambia la plantilla activa, ajustar offset para centrar la tarjeta activa
    useEffect(() => {
        if (plantillasGuardadas.length === 0) return;
        setCarouselOffset(-plantillaActiva * CARD_STEP);
    }, [plantillaActiva, plantillasGuardadas.length, CARD_STEP]);

    const eliminarPlantilla = async (plantillaId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) return;

        try {
            const token = sessionStorage.getItem('token');
            if (!token) {
                alert('⚠️ Debes iniciar sesión para eliminar plantillas');
                return;
            }

            await axios.delete(
                `${process.env.REACT_APP_API_URL}/plantillas-etiquetas/${plantillaId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const plantillasActualizadas = plantillasGuardadas.filter(p => p.id !== plantillaId);
            setPlantillasGuardadas(plantillasActualizadas);

            // Actualizar backup en localStorage
            localStorage.setItem('etiquetaPlantillas', JSON.stringify(plantillasActualizadas));

            alert('✅ Plantilla eliminada correctamente');
        } catch (error) {
            console.error('Error al eliminar plantilla:', error);
            alert('❌ Error al eliminar la plantilla. Intenta nuevamente.');
        }
    };

    // Bloquear scroll del body cuando el modal está abierto
    useEffect(() => {
        if (isOpen) {
            // Guardar el overflow original
            const originalOverflow = document.body.style.overflow;
            const originalPaddingRight = document.body.style.paddingRight;

            // Bloquear scroll
            document.body.style.overflow = 'hidden';
            // Compensar el ancho de la scrollbar para evitar saltos
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`;
            }

            // Cleanup al desmontar o cerrar
            return () => {
                document.body.style.overflow = originalOverflow;
                document.body.style.paddingRight = originalPaddingRight;
            };
        }
    }, [isOpen]);


    if (!isOpen) return null;

    // Usar pedido real si está disponible, sino usar ejemplo
    const pedidoParaPreview = pedidosReales.length > 0
        ? pedidosReales[pedidoPreviewIndex]
        : {
            id_pedido: 123,
            nombreuser: 'Juan',
            apellido: 'Pérez',
            nombre: 'Milanesa con Papas',
            sector: 'Producción',
            ubicacion: 'Planta A',
            fecha: new Date().toISOString(),
        };

    return (
        <div className="modal-overlay">
            <div className="modal-configurador-etiquetas">
                <div className="modal-header">
                    <h2>⚙️ Configurador de Etiquetas</h2>
                    <button onClick={onClose} className="close-button">
                        <X size={24} />
                    </button>
                </div>

                <div className="configurador-content">
                    {/* Panel izquierdo: Controles */}
                    <div className="panel-controles">
                        <div className="seccion">
                            <h3>Campos Disponibles</h3>
                            <div className="campos-disponibles">
                                {CAMPOS_DISPONIBLES.map(campo => (
                                    <button
                                        key={campo.id}
                                        onClick={() => agregarCampo(campo.id)}
                                        className="btn-agregar-campo"
                                        title={`Agregar ${campo.label}`}
                                    >
                                        <Plus size={16} /> {campo.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="seccion">
                            <h3>Campos en Etiqueta</h3>
                            <div className="lista-campos">
                                {campos.map((campo, index) => (
                                    <div
                                        key={index}
                                        className={`campo-item ${selectedCampo === index ? 'selected' : ''}`}
                                        onClick={() => setSelectedCampo(index)}
                                    >
                                        <div className="campo-header">
                                            <span>{campo.label}</span>
                                            <div className="campo-actions">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); toggleVisibilidad(index); }}
                                                    className="btn-icon"
                                                    title={campo.visible ? 'Ocultar' : 'Mostrar'}
                                                >
                                                    {campo.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); eliminarCampo(index); }}
                                                    className="btn-icon btn-danger"
                                                    title="Eliminar"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </div>

                                        {selectedCampo === index && (
                                            <div className="campo-propiedades">
                                                <label>
                                                    Posición X (mm):
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={LABEL_WIDTH}
                                                        step="0.5"
                                                        value={campo.x}
                                                        onChange={(e) => actualizarCampo(index, 'x', parseFloat(e.target.value))}
                                                    />
                                                </label>
                                                <label>
                                                    Posición Y (mm):
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max={LABEL_HEIGHT}
                                                        step="0.5"
                                                        value={campo.y}
                                                        onChange={(e) => actualizarCampo(index, 'y', parseFloat(e.target.value))}
                                                    />
                                                </label>

                                                {campo.id !== 'qrCode' && campo.id !== 'barcode' && campo.id !== 'separador' && (
                                                    <>
                                                        <label>
                                                            Tamaño Fuente:
                                                            <input
                                                                type="number"
                                                                min="8"
                                                                max="50"
                                                                value={campo.fontSize}
                                                                onChange={(e) => actualizarCampo(index, 'fontSize', parseInt(e.target.value))}
                                                            />
                                                        </label>
                                                        <label>
                                                            Rotación:
                                                            <select
                                                                value={campo.rotation || 0}
                                                                onChange={(e) => actualizarCampo(index, 'rotation', parseInt(e.target.value))}
                                                                className="select-rotation"
                                                            >
                                                                <option value={0}>0° (Normal)</option>
                                                                <option value={90}>90° (Derecha)</option>
                                                                <option value={180}>180° (Invertido)</option>
                                                                <option value={270}>270° (Izquierda)</option>
                                                            </select>
                                                        </label>
                                                    </>
                                                )}

                                                {campo.id === 'qrCode' && (
                                                    <label>
                                                        Tamaño QR (mm):
                                                        <input
                                                            type="number"
                                                            min="20"
                                                            max="50"
                                                            value={campo.qrSize || 30}
                                                            onChange={(e) => actualizarCampo(index, 'qrSize', parseInt(e.target.value))}
                                                        />
                                                    </label>
                                                )}

                                                {campo.id === 'barcode' && (
                                                    <>
                                                        <label>
                                                            Altura (mm):
                                                            <input
                                                                type="number"
                                                                min="30"
                                                                max="80"
                                                                value={campo.barcodeHeight || 50}
                                                                onChange={(e) => actualizarCampo(index, 'barcodeHeight', parseInt(e.target.value))}
                                                            />
                                                        </label>
                                                    </>
                                                )}

                                                {campo.id !== 'header' && campo.id !== 'separador' && campo.id !== 'qrCode' && campo.id !== 'barcode' && (
                                                    <label>
                                                        Prefijo:
                                                        <input
                                                            type="text"
                                                            value={campo.prefijo || ''}
                                                            onChange={(e) => actualizarCampo(index, 'prefijo', e.target.value)}
                                                            placeholder="Ej: Nombre: "
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Panel derecho: Vista previa + Carrusel de plantillas */}
                    <div className="panel-preview">
                        {/* Vista Previa siempre visible */}
                        <div className="preview-section">
                            <div className="preview-header">
                                <h3>Vista Previa</h3>
                                {plantillasGuardadas.length > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <label style={{ color: '#cbd5e1', fontSize: 12 }}>Plantilla:</label>
                                        <button
                                            onClick={() => navegarPlantilla('prev')}
                                            className="btn-carousel-nav"
                                            aria-label="Anterior plantilla"
                                        >
                                            ◀
                                        </button>
                                        <select
                                            className="select-plantilla"
                                            value={plantillaActiva}
                                            onChange={(e) => setPlantillaActiva(parseInt(e.target.value, 10))}
                                        >
                                            {plantillasGuardadas.map((p, idx) => (
                                                <option key={p.id} value={idx}>{p.nombre}</option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => navegarPlantilla('next')}
                                            className="btn-carousel-nav"
                                            aria-label="Siguiente plantilla"
                                        >
                                            ▶
                                        </button>
                                        <button
                                            onClick={() => {
                                                const plantilla = plantillasGuardadas[plantillaActiva];
                                                if (plantilla) cargarPlantilla(plantilla);
                                            }}
                                            className="btn-accion btn-success"
                                            style={{ padding: '6px 8px' }}
                                        >
                                            ✓ Cargar
                                        </button>
                                    </div>
                                )}
                                <div className="preview-controls">
                                    {pedidosReales.length > 0 && (
                                        <div className="pedido-selector">
                                            <label>Pedido:</label>
                                            <select
                                                value={pedidoPreviewIndex}
                                                onChange={(e) => setPedidoPreviewIndex(parseInt(e.target.value))}
                                                className="select-pedido"
                                            >
                                                {pedidosReales.map((pedido, idx) => (
                                                    <option key={idx} value={idx}>
                                                        #{pedido.id_pedido} - {pedido.nombreuser} - {pedido.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => setShowPreview(!showPreview)}
                                        className="btn-toggle-preview"
                                    >
                                        {showPreview ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            {showPreview && (
                                <div
                                    id="label-preview"
                                    ref={previewContainerRef}
                                    className="label-preview"
                                    style={{
                                        width: '100%',
                                        maxWidth: 520,
                                        height: 'auto',
                                        border: '2px solid #333',
                                        position: 'relative',
                                        backgroundColor: '#fff',
                                        cursor: isDragging ? 'grabbing' : 'default',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <div className={`label-inner ${previewFading ? 'fading' : ''}`} style={{ width: 400, height: 200, transform: `scale(${previewScale})`, transformOrigin: 'top left', position: 'relative' }}>
                                        {campos.filter(c => c.visible).map((campo, index) => {
                                            const xPercent = (campo.x / LABEL_WIDTH) * 100;
                                            const yPercent = (campo.y / LABEL_HEIGHT) * 100;

                                            let texto = '';
                                            switch (campo.id) {
                                                case 'header':
                                                    texto = 'CIASA';
                                                    break;
                                                case 'nombre':
                                                    const nombreCompleto = `${pedidoParaPreview.nombreuser || ''} ${pedidoParaPreview.apellido || ''}`.trim();
                                                    texto = `${campo.prefijo || ''}${nombreCompleto}`;
                                                    break;
                                                case 'apellido':
                                                    texto = `${campo.prefijo || ''}${pedidoParaPreview.apellido || ''}`;
                                                    break;
                                                case 'plato':
                                                    texto = `${campo.prefijo || ''}${pedidoParaPreview.nombre || ''}`;
                                                    break;
                                                case 'sector':
                                                    texto = `${campo.prefijo || ''}${pedidoParaPreview.sector || ''}`;
                                                    break;
                                                case 'ubicacion':
                                                    texto = `${campo.prefijo || ''}${pedidoParaPreview.ubicacion || ''}`;
                                                    break;
                                                case 'sede':
                                                    const sedeName = pedidoParaPreview.sede?.nombre_sede || pedidoParaPreview.sede?.direccion || 'Sede Principal';
                                                    texto = `${campo.prefijo || ''}${sedeName}`;
                                                    break;
                                                case 'fechaVencimiento':
                                                    const fecha = new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000);
                                                    texto = `${campo.prefijo || ''}${fecha.toLocaleDateString('es-AR')}`;
                                                    break;
                                                default:
                                                    texto = campo.label;
                                            }

                                            // Línea separadora
                                            if (campo.id === 'separador') {
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            position: 'absolute',
                                                            left: `${xPercent}%`,
                                                            top: `${yPercent}%`,
                                                            width: '80%',
                                                            height: '2px',
                                                            backgroundColor: '#000',
                                                            cursor: 'grab',
                                                        }}
                                                        onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                                    />
                                                );
                                            }

                                            // Código QR
                                            if (campo.id === 'qrCode') {
                                                const qrSize = ((campo.qrSize || 30) / LABEL_HEIGHT) * 100;
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            position: 'absolute',
                                                            left: `${xPercent}%`,
                                                            top: `${yPercent}%`,
                                                            width: `${qrSize}%`,
                                                            height: `${qrSize}%`,
                                                            border: '2px solid #000',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'grab',
                                                            backgroundColor: '#fff',
                                                        }}
                                                        onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                                    >
                                                        <QrCode size={20} />
                                                    </div>
                                                );
                                            }

                                            // Código de Barras
                                            if (campo.id === 'barcode') {
                                                const barcodeWidth = 35;
                                                const barcodeHeight = ((campo.barcodeHeight || 50) / LABEL_HEIGHT) * 100;
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            position: 'absolute',
                                                            left: `${xPercent}%`,
                                                            top: `${yPercent}%`,
                                                            width: `${barcodeWidth}%`,
                                                            height: `${barcodeHeight}%`,
                                                            border: '1px solid #000',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'grab',
                                                            backgroundColor: '#fff',
                                                        }}
                                                        onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                                    >
                                                        <div style={{ fontSize: '8px', fontFamily: 'monospace' }}>|||||||||||||||</div>
                                                        <div style={{ fontSize: '8px' }}>#{pedidoParaPreview.id_pedido || '000'}</div>
                                                    </div>
                                                );
                                            }

                                            // Texto normal
                                            const rotation = campo.rotation || 0;
                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${xPercent}%`,
                                                        top: `${yPercent}%`,
                                                        fontSize: `${campo.fontSize * 1.2}px`,
                                                        fontWeight: campo.id === 'header' ? 'bold' : 'normal',
                                                        cursor: 'grab',
                                                        userSelect: 'none',
                                                        border: selectedCampo === campos.indexOf(campo) ? '2px dashed #007bff' : 'none',
                                                        padding: '2px',
                                                        transform: `rotate(${rotation}deg)`,
                                                        transformOrigin: 'left top',
                                                        color: '#000',
                                                        lineHeight: '1.2',
                                                    }}
                                                    onMouseDown={(e) => handleMouseDown(campos.indexOf(campo), e)}
                                                >
                                                    {texto}
                                                    {selectedCampo === campos.indexOf(campo) && (
                                                        <Move
                                                            size={12}
                                                            style={{
                                                                position: 'absolute',
                                                                top: '-15px',
                                                                left: '0',
                                                                color: '#007bff',
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="preview-info">
                                <p><strong>Dimensiones:</strong> {LABEL_WIDTH}mm x {LABEL_HEIGHT}mm</p>
                                <p><strong>Resolución:</strong> {DPI} DPI</p>
                                <p><strong>Tip:</strong> Haz clic y arrastra los campos en la vista previa para reposicionarlos</p>
                            </div>
                        </div>

                        {/* Carrusel 3D de Plantillas - Siempre visible debajo de la vista previa */}
                        {plantillasGuardadas.length > 0 ? (
                            <div className="carousel-integrado-container">
                                <div className="carousel-integrado-header">
                                    <h3>✨ Mis Plantillas</h3>
                                </div>

                                <div
                                    className={`carousel-3d-integrado ${isDraggingCarousel ? 'dragging' : ''}`}
                                    onMouseDown={handleCarouselMouseDown}
                                    onWheel={handleCarouselWheel}
                                    style={{ cursor: isDraggingCarousel ? 'grabbing' : 'grab' }}
                                >
                                    <div
                                        className={`carousel-track-integrado ${isDraggingCarousel ? 'dragging' : ''}`}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            transform: `translateX(${carouselOffset}px)`,
                                            transition: isDraggingCarousel ? 'none' : 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                                        }}
                                    >
                                        {plantillasGuardadas.map((plantilla, index) => {
                                            const isActive = index === plantillaActiva;

                                            return (
                                                <div
                                                    key={plantilla.id}
                                                    className={`plantilla-card-integrada ${isActive ? 'active' : ''}`}
                                                    style={{
                                                        transform: isActive ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(4px)',
                                                        opacity: isActive ? 1 : 0.8,
                                                        margin: '0 6px',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => setPlantillaActiva(index)}
                                                >
                                                    <div className="etiqueta-preview-carousel">
                                                        <div className="etiqueta-simulada-carousel">
                                                            {plantilla.campos.filter(c => c.visible).slice(0, 10).map((campo, idx) => {
                                                                const pedidoEjemplo = {
                                                                    nombreuser: 'Juan',
                                                                    apellido: 'Pérez',
                                                                    nombre: 'Milanesa c/Papas',
                                                                    sector: 'Producción',
                                                                    ubicacion: 'Planta A',
                                                                    id_pedido: 123,
                                                                    fecha: new Date().toISOString()
                                                                };

                                                                let contenido = '';
                                                                switch (campo.id) {
                                                                    case 'header':
                                                                        contenido = 'CIASA';
                                                                        break;
                                                                    case 'nombre':
                                                                        const nombreCompletoCarousel = `${pedidoEjemplo.nombreuser} ${pedidoEjemplo.apellido}`.trim();
                                                                        contenido = `${campo.prefijo || ''}${nombreCompletoCarousel}`;
                                                                        break;
                                                                    case 'apellido':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.apellido}`;
                                                                        break;
                                                                    case 'plato':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.nombre}`;
                                                                        break;
                                                                    case 'sector':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.sector}`;
                                                                        break;
                                                                    case 'ubicacion':
                                                                        contenido = `${campo.prefijo || ''}${pedidoEjemplo.ubicacion}`;
                                                                        break;
                                                                    case 'sede':
                                                                        const sedeNombre = pedidoEjemplo.sede?.nombre_sede || pedidoEjemplo.sede?.direccion || 'Sede Principal';
                                                                        contenido = `${campo.prefijo || ''}${sedeNombre}`;
                                                                        break;
                                                                    case 'fechaVencimiento':
                                                                        const fecha = new Date();
                                                                        fecha.setDate(fecha.getDate() + 7);
                                                                        contenido = `${campo.prefijo || ''}${fecha.toLocaleDateString('es-AR')}`;
                                                                        break;
                                                                    case 'qrCode':
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: `${(campo.x / 100) * 100}%`,
                                                                                    top: `${(campo.y / 50) * 100}%`,
                                                                                    width: '25%',
                                                                                    height: '25%',
                                                                                }}
                                                                            >
                                                                                <div className="qr-preview-carousel">QR</div>
                                                                            </div>
                                                                        );
                                                                    case 'barcode':
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: `${(campo.x / 100) * 100}%`,
                                                                                    top: `${(campo.y / 50) * 100}%`,
                                                                                    width: '35%',
                                                                                    height: '20%',
                                                                                }}
                                                                            >
                                                                                <div className="barcode-preview-carousel">|||||||</div>
                                                                            </div>
                                                                        );
                                                                    case 'separador':
                                                                        return (
                                                                            <div
                                                                                key={idx}
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: `${(campo.x / 100) * 100}%`,
                                                                                    top: `${(campo.y / 50) * 100}%`,
                                                                                    width: '80%',
                                                                                    height: '1px',
                                                                                    background: '#000',
                                                                                }}
                                                                            />
                                                                        );
                                                                    default:
                                                                        contenido = campo.label;
                                                                }

                                                                return (
                                                                    <div
                                                                        key={idx}
                                                                        className="campo-etiqueta-carousel"
                                                                        style={{
                                                                            position: 'absolute',
                                                                            left: `${(campo.x / 100) * 100}%`,
                                                                            top: `${(campo.y / 50) * 100}%`,
                                                                            fontSize: `${campo.fontSize * 0.38}px`,
                                                                            fontWeight: campo.id === 'header' ? 'bold' : 'normal',
                                                                            transform: `rotate(${campo.rotation || 0}deg)`,
                                                                            transformOrigin: 'left top',
                                                                        }}
                                                                    >
                                                                        {contenido}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Información de la plantilla activa */}
                                <div className="plantilla-info-integrada">
                                    <h4>{plantillasGuardadas[plantillaActiva].nombre}</h4>
                                    <p className="plantilla-detalles">
                                        {plantillasGuardadas[plantillaActiva].campos.length} campos • 100x50mm •
                                        {' ' + new Date(plantillasGuardadas[plantillaActiva].fecha).toLocaleDateString('es-AR')}
                                    </p>
                                </div>

                                {/* Controles del carrusel */}
                                <div className="carousel-controls-integrado">
                                    <div className="carousel-nav-center">
                                        <button
                                            onClick={() => navegarPlantilla('prev')}
                                            className="carousel-nav-btn-integrado prev-btn"
                                            disabled={plantillasGuardadas.length <= 1}
                                            aria-label="Anterior plantilla"
                                        >
                                            ◀
                                        </button>

                                        <button
                                            onClick={() => navegarPlantilla('next')}
                                            className="carousel-nav-btn-integrado next-btn"
                                            disabled={plantillasGuardadas.length <= 1}
                                            aria-label="Siguiente plantilla"
                                        >
                                            ▶
                                        </button>
                                    </div>

                                    <div className="carousel-info-integrado">
                                        <div className="carousel-counter-integrado">
                                            {plantillaActiva + 1} / {plantillasGuardadas.length}
                                        </div>
                                        <div className="carousel-indicators-integrado">
                                            {plantillasGuardadas.map((_, index) => (
                                                <button
                                                    key={index}
                                                    className={`indicator-integrado ${index === plantillaActiva ? 'active' : ''}`}
                                                    onClick={() => setPlantillaActiva(index)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Acciones de la plantilla activa */}
                                <div className="plantilla-acciones-integradas">
                                    <button
                                        onClick={() => cargarPlantilla(plantillasGuardadas[plantillaActiva])}
                                        className="btn-accion-integrada btn-cargar"
                                    >
                                        ✓ Cargar Plantilla
                                    </button>
                                    <button
                                        onClick={() => eliminarPlantilla(plantillasGuardadas[plantillaActiva].id)}
                                        className="btn-accion-integrada btn-eliminar"
                                    >
                                        🗑️ Eliminar
                                    </button>
                                </div>

                                <div className="carousel-hint-integrado">
                                    💡 Arrastra, usa la rueda del mouse, las flechas o los indicadores
                                </div>
                            </div>
                        ) : (
                            <div className="no-plantillas-message" style={{
                                padding: '40px 20px',
                                textAlign: 'center',
                                color: '#94a3b8',
                                background: 'linear-gradient(135deg, rgba(51,65,85,0.3) 0%, rgba(30,41,59,0.5) 100%)',
                                borderRadius: '12px',
                                border: '2px dashed #475569',
                                marginTop: '20px'
                            }}>
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                                <h4 style={{ color: '#cbd5e1', marginBottom: '8px' }}>No hay plantillas guardadas</h4>
                                <p style={{ fontSize: '14px', marginBottom: '16px' }}>
                                    Configura tus campos y guarda tu primera plantilla usando el botón
                                    <br />
                                    <strong style={{ color: '#10b981' }}>💾 Guardar como Plantilla</strong>
                                </p>
                                <p style={{ fontSize: '12px', color: '#64748b' }}>
                                    Las plantillas te permiten guardar diferentes configuraciones de etiquetas
                                    <br />
                                    y alternar rápidamente entre ellas
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sticky footer: copy / save / close - always visible */}
                <div className="modal-footer">
                    <button
                        onClick={() => { navigator.clipboard && navigator.clipboard.writeText(JSON.stringify({ campos })); alert('Configuración copiada al portapapeles'); }}
                        className="btn-accion btn-secondary"
                    >
                        <Download size={16} /> Copiar JSON
                    </button>

                    <button onClick={guardarConfiguracion} className="btn-accion btn-primary">
                        <Save size={16} /> Guardar
                    </button>

                    <button onClick={abrirModalNombre} className="btn-accion btn-success">
                        <Save size={16} /> Guardar como Plantilla
                    </button>

                    <div style={{ flex: 1 }} />

                    <button onClick={onClose} className="btn-accion btn-cancel">
                        Cerrar
                    </button>
                </div>
            </div>

            {/* Mini Modal para nombre de plantilla */}
            {showNombreModal && (
                <div className="mini-modal-overlay" onClick={() => setShowNombreModal(false)}>
                    <div className="mini-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="mini-modal-header">
                            <h3>💾 Guardar Plantilla</h3>
                            <button onClick={() => setShowNombreModal(false)} className="close-button">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mini-modal-body">
                            <label>
                                <strong>Nombre de la plantilla:</strong>
                            </label>
                            <input
                                type="text"
                                placeholder="Ej: Plantilla Comedor"
                                value={plantillaNombre}
                                onChange={(e) => setPlantillaNombre(e.target.value)}
                                className="input-plantilla-modal"
                                autoFocus
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && plantillaNombre.trim()) {
                                        guardarComoPlantilla();
                                    }
                                }}
                            />
                        </div>
                        <div className="mini-modal-footer">
                            <button onClick={() => setShowNombreModal(false)} className="btn-modal btn-cancel">
                                Cancelar
                            </button>
                            <button
                                onClick={guardarComoPlantilla}
                                className="btn-modal btn-save"
                                disabled={!plantillaNombre.trim()}
                            >
                                <Save size={16} /> Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfiguradorEtiquetas;
